import {
  GeneralStatus,
  HABILITATION_STATUS_VALUES,
  MEDICAL_VISIT_OPINION_VALUES,
  MEDICAL_VISIT_STATUS_VALUES,
  onDemandUserDocumentTypes,
  supportedLangs,
  TRAINING_STATUS_VALUES,
  USER_COMPLIANCE_STATUS_VALUES,
} from '@geartracker/types';

import { createZodDto } from './zod';

import { z } from 'zod';

const TransferCertificateEquipmentSchema = z.object({
  id: z.number().int(),
  name: z.string().min(1),
  serialNumber: z.string().nullable(),
  partNumber: z.string().nullable(),
  missing: z.boolean(),
});

const TransferCertificatePersonSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
});

// The recipient is the one whose acceptance the certificate attests to, so
// unlike the sender's, their email is required — it identifies the signer,
// not just contact info.
const TransferCertificateSignerSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().min(1),
  registrationNumber: z.string().nullable(),
});

export const TransferCertificateDataSchema = z
  .object({
    transferId: z.number().int(),
    transferExternalId: z.string().nullable(),
    organizationName: z.string().min(1),
    fromUser: TransferCertificatePersonSchema,
    toUser: TransferCertificateSignerSchema,
    equipments: z.array(TransferCertificateEquipmentSchema).min(1),
    issuedAt: z.coerce.date(),
    completedAt: z.coerce.date(),
    receiverComment: z.string().nullable(),
    verificationUrl: z.string().url(),
    lang: z.enum(supportedLangs),
  })
  .refine((data) => data.equipments.some((e) => !e.missing), {
    message: 'A transfer certificate needs at least one non-missing item',
    path: ['equipments'],
  });

export class TransferCertificateDataDto extends createZodDto(
  TransferCertificateDataSchema,
) {}

export type TransferCertificateData = z.infer<
  typeof TransferCertificateDataSchema
>;

// ---------------------------------------------------------------------------
// On-demand user documents (compliance record, habilitation title, PPE issue
// record). Unlike the transfer certificate — a side effect of an accepted,
// immutable transfer — these are snapshots of live data, produced when someone
// asks for them from a user's profile.
// ---------------------------------------------------------------------------

const DocumentSubjectSchema = z.object({
  id: z.number().int(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
  /** User.externalUserId — the employee number printed on the document. */
  registrationNumber: z.string().nullable(),
});

const DocumentIssuerSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
});

const userDocumentBaseShape = {
  organizationName: z.string().min(1),
  subject: DocumentSubjectSchema,
  issuedAt: z.coerce.date(),
  issuedBy: DocumentIssuerSchema,
  lang: z.enum(supportedLangs),
};

const DocumentEquipmentSchema = z.object({
  id: z.number().int(),
  name: z.string().min(1),
  brand: z.string().nullable(),
  model: z.string().nullable(),
  serialNumber: z.string().nullable(),
  partNumber: z.string().nullable(),
  status: z.enum(GeneralStatus),
  nextInspectionDate: z.coerce.date().nullable(),
  expirationDate: z.coerce.date().nullable(),
});

const DocumentTrainingSchema = z.object({
  id: z.number().int(),
  name: z.string().min(1),
  date: z.coerce.date(),
  expiryDate: z.coerce.date().nullable(),
  status: z.enum(TRAINING_STATUS_VALUES),
});

const DocumentHabilitationSchema = z.object({
  id: z.number().int(),
  label: z.string().min(1),
  /** HabilitationType.description — documented as the standard / legal basis. */
  description: z.string().nullable(),
  date: z.coerce.date(),
  nextDueDate: z.coerce.date().nullable(),
  status: z.enum(HABILITATION_STATUS_VALUES),
  training: DocumentTrainingSchema.pick({
    name: true,
    date: true,
    expiryDate: true,
  }).nullable(),
});

/**
 * This schema is the privacy boundary for occupational health data. Nothing
 * beyond the fitness opinion and the OPERATIONAL restriction may ever be added
 * here — no diagnosis, no medical reason, no practitioner name. A compliance
 * document is read by managers and auditors, not by occupational physicians.
 */
const DocumentMedicalVisitSchema = z.object({
  typeLabel: z.string().min(1),
  /** null when the type is assigned to the user but no visit happened yet. */
  date: z.coerce.date().nullable(),
  nextDueDate: z.coerce.date().nullable(),
  opinion: z.enum(MEDICAL_VISIT_OPINION_VALUES).nullable(),
  restriction: z.string().nullable(),
  status: z.enum(MEDICAL_VISIT_STATUS_VALUES).nullable(),
});

export const ComplianceRecordDataSchema = z.object({
  ...userDocumentBaseShape,
  overallStatus: z.enum(USER_COMPLIANCE_STATUS_VALUES),
  equipmentStatus: z.enum(USER_COMPLIANCE_STATUS_VALUES),
  trainingStatus: z.enum(USER_COMPLIANCE_STATUS_VALUES),
  habilitationStatus: z.enum(USER_COMPLIANCE_STATUS_VALUES),
  medicalVisitStatus: z.enum(USER_COMPLIANCE_STATUS_VALUES),
  equipments: z.array(DocumentEquipmentSchema),
  trainings: z.array(DocumentTrainingSchema),
  habilitations: z.array(DocumentHabilitationSchema),
  // Left out entirely (not []) when the organization does not track medical
  // visits, so the template can tell "tracking off" — omit the whole section —
  // from "tracking on, nothing recorded yet".
  medicalVisits: z.array(DocumentMedicalVisitSchema).optional(),
});

export const HabilitationTitleDataSchema = z.object({
  ...userDocumentBaseShape,
  habilitations: z.array(DocumentHabilitationSchema).min(1),
  /**
   * Earliest non-null nextDueDate. Null when every listed habilitation is
   * one-off (HabilitationType.periodicityYears is null) — the template then
   * omits the line rather than printing an invented date.
   */
  validUntil: z.coerce.date().nullable(),
  /** One-line reminder: medical fitness is one of the three pillars of R.4323-56. */
  medicalFitness: DocumentMedicalVisitSchema.nullable(),
});

export const PpeIssueRecordDataSchema = z.object({
  ...userDocumentBaseShape,
  equipments: z
    .array(
      DocumentEquipmentSchema.extend({
        categoryName: z.string().nullable(),
        commissioningDate: z.coerce.date().nullable(),
        /**
         * Latest EQUIPMENT_ALLOCATED_TO_USER event. Null on equipment assigned
         * by import or before that event existed — printed as a dash, never a
         * reason to fail.
         */
        issuedAt: z.coerce.date().nullable(),
      }),
    )
    .min(1),
});

export class ComplianceRecordDataDto extends createZodDto(
  ComplianceRecordDataSchema,
) {}
export class HabilitationTitleDataDto extends createZodDto(
  HabilitationTitleDataSchema,
) {}
export class PpeIssueRecordDataDto extends createZodDto(
  PpeIssueRecordDataSchema,
) {}

export type ComplianceRecordData = z.infer<typeof ComplianceRecordDataSchema>;
export type HabilitationTitleData = z.infer<typeof HabilitationTitleDataSchema>;
export type PpeIssueRecordData = z.infer<typeof PpeIssueRecordDataSchema>;

/** Shared by the preview query and the generation body — one list, one source. */
export const GenerateUserDocumentSchema = z.object({
  documentType: z.enum(onDemandUserDocumentTypes),
});

export class GenerateUserDocumentDto extends createZodDto(
  GenerateUserDocumentSchema,
) {}

export type GenerateUserDocumentInput = z.infer<
  typeof GenerateUserDocumentSchema
>;
